/**
 * TUVI - Seguimiento Comercial (Gerencia)
 * Dashboard de metas y proyectos vendidos mes a mes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './SeguimientoComercial.css';

const MESES_NOMBRES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                       'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const fmt    = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)}%`;
const fmtNum = (v) => new Intl.NumberFormat('es-CO').format(v || 0);

// Barra de cumplimiento. Sin meta no hay cumplimiento que medir: se muestra
// "Sin meta" en vez de un 0 % que parece un mal resultado.
function Cumplimiento({ real, meta }) {
  if (!(meta > 0)) return <span className="sc-sin-meta">Sin meta</span>;
  const c = real / meta;
  return (
    <div className="sc-cumpl-wrap">
      <div className="sc-cumpl-bar">
        <div className="sc-cumpl-fill" style={{ width: `${Math.min(c * 100, 100)}%`, background: c >= 1 ? '#10b981' : '#3b82f6' }} />
      </div>
      <span className={c >= 1 ? 'sc-pct-ok' : 'sc-pct-bajo'}>{fmtPct(c)}</span>
    </div>
  );
}

const ESTADO_LABELS = {
  vendido: 'Vendido', contratado: 'Contratado', en_ejecucion: 'En Ejecución',
  cobrado_liquidado: 'Cobrado', entregado_liquidado: 'Entregado'
};
const ESTADO_COLORS = {
  vendido: '#8b5cf6', contratado: '#f59e0b', en_ejecucion: '#3b82f6',
  cobrado_liquidado: '#10b981', entregado_liquidado: '#6b7280'
};

// Mapea estados de lista-proyectos al ENUM de seguimiento
const mapearEstado = (estadoProyecto) => {
  const mapa = {
    vendido: 'vendido',
    contratado_no_iniciado: 'contratado',
    en_ejecucion: 'en_ejecucion',
    cobrado_liquidado: 'cobrado_liquidado',
    entregado_liquidado: 'entregado_liquidado'
  };
  return mapa[estadoProyecto] || 'vendido';
};

const anioActual = new Date().getFullYear();
const mesActual  = new Date().getMonth() + 1;

export default function SeguimientoComercial() {
  const { success, error } = useNotification();

  // Vista: 'anual' | 'mensual'
  const [vista, setVista]         = useState('anual');
  const anio                      = anioActual; // siempre el año actual
  const [metaActiva, setMetaActiva] = useState(null);
  const [resumen, setResumen]     = useState([]);
  const [totales, setTotales]     = useState({});
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading]     = useState(false);

  // Modal editar meta
  const [modalMeta, setModalMeta] = useState(false);
  const [formMeta, setFormMeta]   = useState({});

  // Modal agregar / editar proyecto
  const [modalProy, setModalProy] = useState(false); // false | 'add' | 'edit'
  const [formProy, setFormProy]   = useState({});
  const [guardando, setGuardando] = useState(false);

  // Selector de proyecto desde lista
  const [modoModal, setModoModal]         = useState('manual');  // 'manual' | 'lista'
  const [proyectosLista, setProyectosLista] = useState([]);
  const [loadingLista, setLoadingLista]   = useState(false);
  const [busquedaLista, setBusquedaLista] = useState('');

  // Confirmar eliminación
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ── CARGA ANUAL ──────────────────────────────────────────────
  const cargarAnual = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(`query($anio: Int!) { seguimientoAnual(anio: $anio) { id anio mes meta_ventas meta_valor meta_contratados ventas_reales valor_real contratados_reales margen_real margen_pct costos_gastos } }`, { anio });
      // Mapear al formato que espera el componente
      const mapped = (d.seguimientoAnual || []).map(m => {
        const unidades_meta = m.meta_ventas || 0;
        const ventas_meta = m.meta_valor || 0;
        const unidades_real = m.ventas_reales || 0;
        const ventas_real = m.valor_real || 0;
        const costos_gastos = m.costos_gastos || 0;
        // Margen real de cada negocio (rentabilidad − comisiones), el mismo del detalle del mes
        const margen_real = m.margen_real || 0;
        const neto = margen_real - costos_gastos;
        return {
          ...m,
          unidades_meta, ventas_meta, unidades_real, ventas_real,
          cumpl_unidades: unidades_meta > 0 ? unidades_real / unidades_meta : 0,
          cumpl_ventas: ventas_meta > 0 ? ventas_real / ventas_meta : 0,
          margen_real, costos_gastos, neto,
          margen_real_pct: ventas_real > 0 ? margen_real / ventas_real : 0,
        };
      });
      setResumen(mapped);
      // Los nombres deben ser los mismos que lee la fila "Total" (antes no
      // coincidían y el total salía en 0).
      const totMargenReal = mapped.reduce((s, m) => s + m.margen_real, 0);
      const totCostos = mapped.reduce((s, m) => s + m.costos_gastos, 0);
      const totValorReal = mapped.reduce((s, m) => s + m.ventas_real, 0);
      setTotales({
        unidades_meta: mapped.reduce((s, m) => s + m.unidades_meta, 0),
        unidades_real: mapped.reduce((s, m) => s + m.unidades_real, 0),
        ventas_meta: mapped.reduce((s, m) => s + m.ventas_meta, 0),
        ventas_real: totValorReal,
        margen_real: totMargenReal,
        costos_gastos: totCostos,
        neto: totMargenReal - totCostos,
        margen_real_pct: totValorReal > 0 ? totMargenReal / totValorReal : 0,
      });
    } catch (e) { error('Error cargando el seguimiento: ' + (e.message || '')); }
    finally { setLoading(false); }
  }, [anio, error]);

  useEffect(() => { cargarAnual(); }, [cargarAnual]);

  // ── CARGAR PROYECTOS DE LISTA ─────────────────────────────────
  const cargarProyectosLista = useCallback(async () => {
    if (proyectosLista.length > 0) return; // ya cargados
    setLoadingLista(true);
    try {
      const d = await client.query(`{ proyectos { id proyecto_nombre nombre_cliente apartamento valor_final estado comercial } }`);
      setProyectosLista(d.proyectos || []);
    } catch { /* silencioso */ }
    finally { setLoadingLista(false); }
  }, [proyectosLista.length]);

  // ── ABRIR MES ────────────────────────────────────────────────
  const abrirMes = async (mes) => {
    setLoading(true);
    try {
      const d1 = await client.mutate(`mutation($anio: Int!, $mes: Int!) { asegurarMesSeguimiento(anio: $anio, mes: $mes) { success message meta { id anio mes meta_ventas meta_ingresos } } }`, { anio, mes });
      if (!d1.asegurarMesSeguimiento.success) { error(d1.asegurarMesSeguimiento.message); setLoading(false); return; }

      const d2 = await client.query(`query($mid: Int!) { seguimientoDetalleMes(meta_id: $mid) }`, { mid: d1.asegurarMesSeguimiento.meta.id });
      if (d2.seguimientoDetalleMes) {
        setMetaActiva(d2.seguimientoDetalleMes.meta || d1.asegurarMesSeguimiento.meta);
        setProyectos(d2.seguimientoDetalleMes.proyectos || []);
        setVista('mensual');
      } else error('Error cargando detalle');
    } catch { error('Error de conexión'); }
    finally { setLoading(false); }
  };

  // ── RECARGAR DETALLE MES ─────────────────────────────────────
  const recargarMes = async (meta_id) => {
    const d = await client.query(`query($mid: Int!) { seguimientoDetalleMes(meta_id: $mid) }`, { mid: meta_id });
    if (d.seguimientoDetalleMes) { setMetaActiva(d.seguimientoDetalleMes.meta); setProyectos(d.seguimientoDetalleMes.proyectos || []); }
  };

  // ── GUARDAR META ─────────────────────────────────────────────
  // Antes el margen (0.17) se enviaba en un campo entero y el guardado fallaba
  // siempre; los costos no se enviaban. Ahora cada valor va en su campo.
  const guardarMeta = async () => {
    setGuardando(true);
    try {
      const input = {
        unidades_meta: parseInt(formMeta.unidades_meta, 10) || 0,
        ventas_meta: parseFloat(formMeta.ventas_meta) || 0,
        margen_pct: parseFloat(formMeta.margen_pct) || 0,
        costos_gastos: parseFloat(formMeta.costos_gastos) || 0,
        aplicar_siguientes: !!formMeta.aplicar_siguientes,
      };
      const gqlR = await client.mutate(`mutation($mid: Int!, $input: SeguimientoMetaInput!) { actualizarMetaSeguimiento(meta_id: $mid, input: $input) { success message } }`, { mid: metaActiva.id, input });
      const d = gqlR.actualizarMetaSeguimiento;
      if (d.success) {
        success(d.message || 'Meta actualizada');
        setModalMeta(false);
        await recargarMes(metaActiva.id);
        cargarAnual();
      } else error(d.message);
    } catch (e) { error('No se pudo guardar la meta: ' + (e.message || '')); }
    finally { setGuardando(false); }
  };

  // ── GUARDAR PROYECTO ─────────────────────────────────────────
  const guardarProyecto = async () => {
    if (!formProy.nombre_proyecto?.trim()) { error('El nombre del proyecto es obligatorio'); return; }
    setGuardando(true);
    try {
      const esEdicion = !!formProy.id;
      const inputProy = {
        nombre_cliente: formProy.nombre_cliente || '', nombre_proyecto: formProy.nombre_proyecto,
        apartamento: formProy.apartamento || '', valor: parseFloat(formProy.valor) || 0,
        rentabilidad_pct: parseFloat(formProy.rentabilidad_pct) || 0,
        comision_ext_pct: parseFloat(formProy.comision_ext_pct) || 0,
        comision_int_pct: parseFloat(formProy.comision_int_pct) || 0,
        pagado: parseFloat(formProy.pagado) || 0, ejecutado: parseFloat(formProy.ejecutado) || 0,
        estado: formProy.estado || 'vendido', proyecto_id: formProy.proyecto_id ? parseInt(formProy.proyecto_id) : null,
        comercial: formProy.comercial || null,
      };

      let d;
      if (esEdicion) {
        const r = await client.mutate(`mutation($id: Int!, $input: ProyectoSeguimientoInput!) { editarProyectoSeguimiento(id: $id, input: $input) { success message } }`, { id: formProy.id, input: inputProy });
        d = r.editarProyectoSeguimiento;
      } else {
        const r = await client.mutate(`mutation($mid: Int!, $input: ProyectoSeguimientoInput!) { agregarProyectoSeguimiento(meta_id: $mid, input: $input) { success message } }`, { mid: metaActiva.id, input: inputProy });
        d = r.agregarProyectoSeguimiento;
      }
      if (d.success) {
        success(esEdicion ? 'Proyecto actualizado' : 'Proyecto agregado');
        setModalProy(false);
        await recargarMes(metaActiva.id);
        cargarAnual();
      } else error(d.message);
    } catch { error('Error de conexión'); }
    finally { setGuardando(false); }
  };

  // ── ELIMINAR PROYECTO ────────────────────────────────────────
  const eliminarProyecto = async (id) => {
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarProyectoSeguimiento(id: $id) { success message } }`, { id });
      const d = gqlR.eliminarProyectoSeguimiento;
      if (d.success) {
        success('Proyecto eliminado');
        setConfirmDelete(null);
        await recargarMes(metaActiva.id);
        cargarAnual();
      } else error(d.message);
    } catch { error('Error de conexión'); }
  };

  // ── ABRIR MODAL PROYECTO ─────────────────────────────────────
  // Convierte número a display con miles (1500000 → "1.500.000")
  const fmtValorDisplay = (v) =>
    v ? new Intl.NumberFormat('es-CO').format(v) : '';

  // Convierte texto con miles a número ("1.500.000" → 1500000)
  const parseValorDisplay = (str) => {
    const raw = str.replace(/\./g, '').replace(/[^\d]/g, '');
    return raw ? parseInt(raw, 10) : '';
  };

  const abrirModalAgregar = () => {
    setFormProy({
      nombre_proyecto: '', apartamento: '',
      valor: '', valorDisplay: '',
      rentabilidad_pct: 0.22, rentabilidad_display: '22',
      comision_ext_pct: 0,    comision_ext_display: '0',
      comision_int_pct: 0.015, comision_int_display: '1.5',
      pagado: 0, ejecutado: 0, estado: 'vendido', proyecto_id: null
    });
    setModoModal('manual');
    setBusquedaLista('');
    setModalProy('add');
  };

  const abrirModalEditar = (p) => {
    setFormProy({
      ...p,
      valorDisplay: fmtValorDisplay(p.valor),
      rentabilidad_display: (((p.rentabilidad_pct || 0) * 100)).toString(),
      comision_ext_display: (((p.comision_ext_pct || 0) * 100)).toString(),
      comision_int_display: (((p.comision_int_pct || 0) * 100)).toString(),
    });
    setModoModal('manual');
    setModalProy('edit');
  };

  // Seleccionar proyecto de la lista existente
  const seleccionarProyectoLista = (proy) => {
    const valor = parseFloat(proy.valor_final) || '';
    setFormProy(prev => ({
      ...prev,
      nombre_proyecto: proy.proyecto_nombre || proy.nombre_proyecto || '',
      apartamento: proy.apartamento || '',
      valor,
      valorDisplay: fmtValorDisplay(valor),
      estado: mapearEstado(proy.estado),
      proyecto_id: proy.id
    }));
    setBusquedaLista('');
  };

  // ── HELPERS ──────────────────────────────────────────────────
  const margenProyecto = (p) =>
    (parseFloat(p.rentabilidad_pct || 0) - parseFloat(p.comision_ext_pct || 0) - parseFloat(p.comision_int_pct || 0));

  const totalesMes = () => {
    const total_val  = proyectos.reduce((s, p) => s + p.valor, 0);
    const total_marg = proyectos.reduce((s, p) => s + p.valor * margenProyecto(p), 0);
    const pagado     = proyectos.reduce((s, p) => s + p.pagado, 0);
    return { total_val, total_marg, pagado, count: proyectos.length };
  };

  // ── RENDER FILA PROYECTO ──────────────────────────────────────
  const renderFilaProy = (p) => {
    const marg = margenProyecto(p);
    return (
      <tr key={p.id} className={`sc-proy-row sc-estado-${p.estado}`}>
        <td className="sc-proy-nombre">
          {p.nombre_proyecto}
          {p.apartamento && <span className="sc-proy-apto">Apto: {p.apartamento}</span>}
        </td>
        <td className="sc-proy-val">{fmt(p.valor)}</td>
        <td className="sc-pct-cell">{fmtPct(p.rentabilidad_pct)}</td>
        <td className="sc-pct-cell">{fmtPct(p.comision_ext_pct)}</td>
        <td className="sc-pct-cell">{fmtPct(p.comision_int_pct)}</td>
        <td className="sc-pct-cell"><strong>{fmtPct(marg)}</strong></td>
        <td className="sc-proy-val sc-positivo">{fmt(p.valor * marg)}</td>
        <td>
          <span className="sc-badge-estado" style={{ background: ESTADO_COLORS[p.estado] || '#9ca3af' }}>
            {ESTADO_LABELS[p.estado] || p.estado}
          </span>
        </td>
        <td className="sc-acciones-cel">
          <button className="sc-btn-edit" onClick={() => abrirModalEditar(p)} title="Editar">✏️</button>
          <button className="sc-btn-del" onClick={() => setConfirmDelete(p)} title="Eliminar">🗑️</button>
        </td>
      </tr>
    );
  };

  // ── RENDER ANUAL ─────────────────────────────────────────────
  const renderAnual = () => (
    <div className="sc-container">
      <div className="sc-header">
        <div>
          <h1>Seguimiento Comercial</h1>
          <p>Metas y resultados de ventas — {anio}</p>
        </div>
      </div>

      {loading ? (
        <div className="sc-loading"><div className="sc-spinner" /><span>Cargando...</span></div>
      ) : (
        <div className="sc-card">
          <table className="sc-table-anual">
            <thead>
              <tr>
                <th>Mes</th>
                <th>Meta Und.</th>
                <th>Meta $</th>
                <th># Vendidas</th>
                <th>$ Vendido</th>
                <th>Cumpl. Und.</th>
                <th>Cumpl. $</th>
                <th>Margen Real $</th>
                <th>Costos</th>
                <th>Neto</th>
                <th>% Margen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {MESES_NOMBRES.map((nombre, idx) => {
                const mes = idx + 1;
                const m   = resumen.find(r => parseInt(r.mes) === mes);
                const esFuturo = mes > mesActual;
                const esActual = mes === mesActual;

                if (!m) {
                  return (
                    <tr key={mes} className="sc-row-vacio">
                      <td className="sc-mes-nombre">{nombre}{esActual && <span className="sc-badge-actual">HOY</span>}</td>
                      <td colSpan={10} style={{ color: '#9ca3af', fontSize: '0.82rem' }}>
                        {esFuturo ? 'Mes futuro' : 'Sin datos'}
                      </td>
                      <td>
                        <button className="sc-btn-abrir" onClick={() => abrirMes(mes)}>
                          {esFuturo ? 'Preparar' : 'Abrir'}
                        </button>
                      </td>
                    </tr>
                  );
                }

                const neto   = m.neto || 0;
                return (
                  <tr key={mes}
                    className={`sc-row ${esActual ? 'sc-row-actual' : ''} ${neto < 0 ? 'sc-row-negativo' : ''}`}
                    onClick={() => abrirMes(mes)}
                    title="Click para ver detalle">
                    <td className="sc-mes-nombre">{nombre}{esActual && <span className="sc-badge-actual">HOY</span>}</td>
                    <td className={m.unidades_meta > 0 ? '' : 'sc-sin-meta'}>{m.unidades_meta > 0 ? fmtNum(m.unidades_meta) : '—'}</td>
                    <td className={m.ventas_meta > 0 ? '' : 'sc-sin-meta'}>{m.ventas_meta > 0 ? fmt(m.ventas_meta) : '—'}</td>
                    <td className="sc-num-real">{m.unidades_real}</td>
                    <td className="sc-num-real">{fmt(m.ventas_real)}</td>
                    <td><Cumplimiento real={m.unidades_real} meta={m.unidades_meta} /></td>
                    <td><Cumplimiento real={m.ventas_real} meta={m.ventas_meta} /></td>
                    <td>{fmt(m.margen_real)}</td>
                    <td>{fmt(m.costos_gastos)}</td>
                    <td className={neto >= 0 ? 'sc-positivo' : 'sc-negativo'}>{fmt(neto)}</td>
                    <td>{fmtPct(m.margen_real_pct)}</td>
                    <td><button className="sc-btn-abrir" onClick={(e) => { e.stopPropagation(); abrirMes(mes); }}>Ver</button></td>
                  </tr>
                );
              })}
            </tbody>
            {resumen.length > 0 && (
              <tfoot>
                <tr className="sc-totales">
                  <td><strong>Total {anio}</strong></td>
                  <td><strong>{fmtNum(totales.unidades_meta)}</strong></td>
                  <td><strong>{fmt(totales.ventas_meta)}</strong></td>
                  <td className="sc-num-real"><strong>{fmtNum(totales.unidades_real)}</strong></td>
                  <td className="sc-num-real"><strong>{fmt(totales.ventas_real)}</strong></td>
                  <td><Cumplimiento real={totales.unidades_real} meta={totales.unidades_meta} /></td>
                  <td><Cumplimiento real={totales.ventas_real} meta={totales.ventas_meta} /></td>
                  <td><strong>{fmt(totales.margen_real)}</strong></td>
                  <td><strong>{fmt(totales.costos_gastos)}</strong></td>
                  <td className={totales.neto >= 0 ? 'sc-positivo' : 'sc-negativo'}><strong>{fmt(totales.neto)}</strong></td>
                  <td><strong>{fmtPct(totales.margen_real_pct)}</strong></td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
          {resumen.length === 0 && !loading && (
            <div className="sc-empty">
              <p>No hay datos para {anio}. Haz click en cualquier mes para comenzar.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── RENDER MENSUAL ────────────────────────────────────────────
  const renderMensual = () => {
    if (!metaActiva) return null;
    const mes    = parseInt(metaActiva.mes);
    const tot    = totalesMes();
    const cumplU = metaActiva.unidades_meta > 0 ? tot.count / metaActiva.unidades_meta : 0;
    const cumplV = metaActiva.ventas_meta > 0 ? tot.total_val / metaActiva.ventas_meta : 0;
    const neto   = tot.total_marg - parseFloat(metaActiva.costos_gastos || 0);

    // Agrupar por estado
    const grupos = [
      { estado: 'entregado_liquidado', label: 'Entregados', list: proyectos.filter(p => p.estado === 'entregado_liquidado') },
      { estado: 'cobrado_liquidado', label: 'Cobrados', list: proyectos.filter(p => p.estado === 'cobrado_liquidado') },
      { estado: 'en_ejecucion', label: 'En Ejecución', list: proyectos.filter(p => p.estado === 'en_ejecucion') },
      { estado: 'contratado',  label: 'Contratados',   list: proyectos.filter(p => p.estado === 'contratado') },
      { estado: 'vendido',     label: 'Vendidos',      list: proyectos.filter(p => p.estado === 'vendido') },
    ].filter(g => g.list.length > 0);

    const mostrarSep = grupos.length > 1;

    return (
      <div className="sc-container">
        {/* Header mensual */}
        <div className="sc-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="sc-btn-volver" onClick={() => { setVista('anual'); cargarAnual(); }}>
              ← Volver
            </button>
            <div>
              <h1>{MESES_NOMBRES[mes - 1]} {anio}</h1>
              <p>Seguimiento mensual de proyectos y resultados</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="sc-btn-meta" onClick={() => {
              setFormMeta({
                unidades_meta:        metaActiva.unidades_meta,
                ventas_meta:          metaActiva.ventas_meta,
                ventas_meta_display:  fmtValorDisplay(metaActiva.ventas_meta),
                margen_pct:           metaActiva.margen_pct,
                margen_display:       ((parseFloat(metaActiva.margen_pct) || 0) * 100).toString(),
                costos_gastos:        metaActiva.costos_gastos,
                costos_gastos_display: fmtValorDisplay(metaActiva.costos_gastos),
                aplicar_siguientes:   false,
              });
              setModalMeta(true);
            }}>
              ⚙️ Editar Meta
            </button>
            <button className="sc-btn-add" onClick={abrirModalAgregar}>
              + Agregar Proyecto
            </button>
          </div>
        </div>

        {/* Conteo por estado */}
        {(() => {
          const countVendido = proyectos.filter(p => p.estado === 'vendido').length;
          const countContratado = proyectos.filter(p => p.estado === 'contratado').length;
          const countEjecucion = proyectos.filter(p => p.estado === 'en_ejecucion').length;
          const countCobrado = proyectos.filter(p => p.estado === 'cobrado_liquidado').length;
          const countEntregado = proyectos.filter(p => p.estado === 'entregado_liquidado').length;
          return (
            <div className="sc-estado-counts">
              <div className="sc-estado-count-item" style={{ borderColor: '#f59e0b' }}>
                <span className="sc-estado-count-num" style={{ color: '#f59e0b' }}>{countVendido}</span>
                <span className="sc-estado-count-label">Vendidos</span>
              </div>
              <div className="sc-estado-count-item" style={{ borderColor: '#8b5cf6' }}>
                <span className="sc-estado-count-num" style={{ color: '#8b5cf6' }}>{countContratado}</span>
                <span className="sc-estado-count-label">Contratados</span>
              </div>
              <div className="sc-estado-count-item" style={{ borderColor: '#3b82f6' }}>
                <span className="sc-estado-count-num" style={{ color: '#3b82f6' }}>{countEjecucion}</span>
                <span className="sc-estado-count-label">En Ejecución</span>
              </div>
              <div className="sc-estado-count-item" style={{ borderColor: '#10b981' }}>
                <span className="sc-estado-count-num" style={{ color: '#10b981' }}>{countCobrado}</span>
                <span className="sc-estado-count-label">Cobrados</span>
              </div>
              <div className="sc-estado-count-item" style={{ borderColor: '#06b6d4' }}>
                <span className="sc-estado-count-num" style={{ color: '#06b6d4' }}>{countEntregado}</span>
                <span className="sc-estado-count-label">Entregados</span>
              </div>
              <div className="sc-estado-count-item sc-estado-count-total">
                <span className="sc-estado-count-num">{tot.count}</span>
                <span className="sc-estado-count-label">
                  {metaActiva.unidades_meta > 0 ? `Total / ${metaActiva.unidades_meta} Meta` : 'Total · sin meta'}
                </span>
                {metaActiva.unidades_meta > 0 && (
                  <span className="sc-estado-cumpl-mini" style={{ background: cumplU >= 1 ? '#dcfce7' : '#fef3c7', color: cumplU >= 1 ? '#166534' : '#92400e' }}>
                    {fmtPct(cumplU)}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Cards resumen financiero */}
        <div className="sc-stats-grid">
          <div className="sc-stat-card sc-stat-green">
            <div className="sc-stat-val">{fmt(tot.total_val)}</div>
            <div className="sc-stat-label">Total vendido</div>
            {metaActiva.ventas_meta > 0 ? (
              <div className="sc-stat-cumpl" style={{ background: cumplV >= 1 ? '#dcfce7' : '#fef3c7', color: cumplV >= 1 ? '#166534' : '#92400e' }}>
                Meta: {fmt(metaActiva.ventas_meta)} · Cumpl: {fmtPct(cumplV)}
              </div>
            ) : (
              <div className="sc-stat-cumpl" style={{ color: '#9ca3af' }}>
                Sin meta en dinero · usa "Editar Meta"
              </div>
            )}
          </div>
          <div className="sc-stat-card sc-stat-purple">
            <div className="sc-stat-val">{fmt(tot.total_marg)}</div>
            <div className="sc-stat-label">Margen bruto</div>
            <div className="sc-stat-cumpl" style={{ color: '#6b7280' }}>
              {tot.total_val > 0 ? fmtPct(tot.total_marg / tot.total_val) : '0.0%'} sobre ventas
            </div>
          </div>
          <div className={`sc-stat-card ${neto >= 0 ? 'sc-stat-green' : 'sc-stat-red'}`}>
            <div className="sc-stat-val">{fmt(neto)}</div>
            <div className="sc-stat-label">Neto (Margen - Costos)</div>
            <div className="sc-stat-cumpl" style={{ color: '#6b7280' }}>
              Costos y gastos: {fmt(metaActiva.costos_gastos)}
            </div>
          </div>
        </div>

        {/* Tabla proyectos */}
        <div className="sc-card">
          {proyectos.length === 0 ? (
            <div className="sc-empty">
              <p>No hay proyectos este mes. Agrega el primero.</p>
            </div>
          ) : (
            <table className="sc-table-proy">
              <thead>
                <tr>
                  <th>Proyecto</th>
                  <th>Valor</th>
                  <th>Rentab.</th>
                  <th>Com. Ext</th>
                  <th>Com. Int</th>
                  <th>Margen</th>
                  <th>$ Margen</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((grupo) => (
                  <React.Fragment key={grupo.estado}>
                    {mostrarSep && (
                      <tr className="sc-sep-row">
                        <td colSpan={9}>
                          <span className="sc-sep-label">{grupo.label} ({grupo.list.length})</span>
                        </td>
                      </tr>
                    )}
                    {grupo.list.map(p => renderFilaProy(p))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="sc-totales">
                  <td><strong>Total ({proyectos.length})</strong></td>
                  <td className="sc-proy-val"><strong>{fmt(tot.total_val)}</strong></td>
                  <td colSpan={4} />
                  <td className="sc-proy-val sc-positivo"><strong>{fmt(tot.total_marg)}</strong></td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    );
  };

  // ── MODAL META ────────────────────────────────────────────────
  const renderModalMeta = () => (
    <div className="sc-modal-overlay" onClick={() => !guardando && setModalMeta(false)}>
      <div className="sc-modal" onClick={e => e.stopPropagation()}>
        <div className="sc-modal-header">
          <h3>Editar Meta — {metaActiva && MESES_NOMBRES[metaActiva.mes - 1]} {anio}</h3>
          <button className="sc-modal-close" onClick={() => setModalMeta(false)}>×</button>
        </div>
        <div className="sc-modal-body">
          <div className="sc-form-grid">
            <div className="sc-form-group">
              <label>Unidades Meta</label>
              <input type="number" value={formMeta.unidades_meta || ''}
                onChange={e => setFormMeta(p => ({ ...p, unidades_meta: e.target.value }))} />
            </div>
            <div className="sc-form-group">
              <label>Ventas Meta (COP)</label>
              <input type="text" inputMode="numeric" placeholder="Ej: 450.000.000"
                value={formMeta.ventas_meta_display || ''}
                onChange={e => {
                  const num = parseValorDisplay(e.target.value);
                  setFormMeta(p => ({ ...p, ventas_meta: num, ventas_meta_display: fmtValorDisplay(num) }));
                }} />
            </div>
            <div className="sc-form-group">
              <label>Margen meta %</label>
              <div className="sc-pct-input-wrap">
                <input type="number" step="0.1" min="0" max="100" placeholder="Ej: 17"
                  value={formMeta.margen_display ?? ''}
                  onChange={e => {
                    const d = e.target.value;
                    setFormMeta(p => ({ ...p, margen_display: d, margen_pct: (parseFloat(d) || 0) / 100 }));
                  }} />
                <span className="sc-pct-suffix">%</span>
              </div>
            </div>
            <div className="sc-form-group">
              <label>Costos y Gastos (COP)</label>
              <input type="text" inputMode="numeric" placeholder="Ej: 90.000.000"
                value={formMeta.costos_gastos_display || ''}
                onChange={e => {
                  const num = parseValorDisplay(e.target.value);
                  setFormMeta(p => ({ ...p, costos_gastos: num, costos_gastos_display: fmtValorDisplay(num) }));
                }} />
            </div>
          </div>
          {metaActiva && metaActiva.mes < 12 && (
            <label className="sc-aplicar-siguientes">
              <input type="checkbox" checked={!!formMeta.aplicar_siguientes}
                onChange={e => setFormMeta(p => ({ ...p, aplicar_siguientes: e.target.checked }))} />
              Aplicar también a los meses siguientes ({MESES_NOMBRES[metaActiva.mes]} a Diciembre {anio})
            </label>
          )}
          <div className="sc-modal-footer">
            <button className="sc-btn-cancel" onClick={() => setModalMeta(false)} disabled={guardando}>Cancelar</button>
            <button className="sc-btn-save" onClick={guardarMeta} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar Meta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── MODAL PROYECTO ────────────────────────────────────────────
  const renderModalProy = () => {
    const proyFiltrados = proyectosLista.filter(p => {
      if (!busquedaLista) return true;
      const q = busquedaLista.toLowerCase();
      return (p.proyecto_nombre || '').toLowerCase().includes(q) ||
             (p.nombre_cliente  || '').toLowerCase().includes(q);
    });

    return (
      <div className="sc-modal-overlay" onClick={() => !guardando && setModalProy(false)}>
        <div className="sc-modal sc-modal-wide" onClick={e => e.stopPropagation()}>
          <div className="sc-modal-header">
            <h3>{modalProy === 'edit' ? 'Editar' : 'Agregar'} Proyecto</h3>
            <button className="sc-modal-close" onClick={() => setModalProy(false)}>×</button>
          </div>
          <div className="sc-modal-body">

            {/* Toggle manual / desde lista (solo en modo agregar) */}
            {modalProy === 'add' && (
              <div className="sc-modo-toggle">
                <button
                  className={`sc-modo-btn ${modoModal === 'manual' ? 'active' : ''}`}
                  onClick={() => setModoModal('manual')}>
                  ✏️ Manual
                </button>
                <button
                  className={`sc-modo-btn ${modoModal === 'lista' ? 'active' : ''}`}
                  onClick={() => {
                    setModoModal('lista');
                    cargarProyectosLista();
                  }}>
                  📋 Desde Lista de Proyectos
                </button>
              </div>
            )}

            {/* Selector de proyecto existente */}
            {modoModal === 'lista' && (
              <div className="sc-lista-selector">
                <input
                  type="text"
                  className="sc-lista-search"
                  placeholder="Buscar proyecto por nombre o cliente..."
                  value={busquedaLista}
                  onChange={e => setBusquedaLista(e.target.value)}
                  autoFocus
                />
                <div className="sc-lista-items">
                  {loadingLista && <div className="sc-lista-msg">Cargando proyectos...</div>}
                  {!loadingLista && proyFiltrados.length === 0 && (
                    <div className="sc-lista-msg">No se encontraron proyectos</div>
                  )}
                  {!loadingLista && proyFiltrados.map(p => (
                    <button key={p.id} className="sc-lista-item" onClick={() => seleccionarProyectoLista(p)}>
                      <span className="sc-lista-nombre">{p.proyecto_nombre || p.nombre_proyecto}</span>
                      <span className="sc-lista-meta">
                        {p.nombre_cliente && <span>{p.nombre_cliente}</span>}
                        {p.valor_final > 0 && <span>{fmt(p.valor_final)}</span>}
                        <span className="sc-badge-estado" style={{ background: ESTADO_COLORS[mapearEstado(p.estado)] || '#9ca3af', fontSize: '0.7rem', padding: '2px 6px' }}>
                          {ESTADO_LABELS[mapearEstado(p.estado)]}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                {formProy.proyecto_id && (
                  <div className="sc-lista-seleccionado">
                    ✅ Seleccionado: <strong>{formProy.nombre_proyecto}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Formulario de datos del seguimiento */}
            <div className="sc-form-grid" style={{ marginTop: modoModal === 'lista' ? '14px' : '0' }}>
              <div className="sc-form-group sc-form-full">
                <label>Nombre del Proyecto *</label>
                <input type="text" placeholder="Ej: Torres del campo 628"
                  value={formProy.nombre_proyecto || ''}
                  onChange={e => setFormProy(p => ({ ...p, nombre_proyecto: e.target.value }))} />
              </div>
              <div className="sc-form-group">
                <label>Apartamento / Unidad</label>
                <input type="text" placeholder="Ej: 628, Torre A piso 6"
                  value={formProy.apartamento || ''}
                  onChange={e => setFormProy(p => ({ ...p, apartamento: e.target.value }))} />
              </div>
              <div className="sc-form-group">
                <label>Valor del Contrato (COP)</label>
                <input type="text" inputMode="numeric" placeholder="Ej: 150.000.000"
                  value={formProy.valorDisplay || ''}
                  onChange={e => {
                    const num = parseValorDisplay(e.target.value);
                    setFormProy(p => ({ ...p, valor: num, valorDisplay: fmtValorDisplay(num) }));
                  }} />
              </div>
              <div className="sc-form-group">
                <label>Rentabilidad %</label>
                <div className="sc-pct-input-wrap">
                  <input type="number" step="0.1" min="0" max="100" placeholder="Ej: 22"
                    value={formProy.rentabilidad_display ?? ''}
                    onChange={e => {
                      const d = e.target.value;
                      setFormProy(p => ({ ...p, rentabilidad_display: d, rentabilidad_pct: (parseFloat(d) || 0) / 100 }));
                    }} />
                  <span className="sc-pct-suffix">%</span>
                </div>
              </div>
              <div className="sc-form-group">
                <label>Comisión Externa %</label>
                <div className="sc-pct-input-wrap">
                  <input type="number" step="0.1" min="0" max="100" placeholder="Ej: 3"
                    value={formProy.comision_ext_display ?? ''}
                    onChange={e => {
                      const d = e.target.value;
                      setFormProy(p => ({ ...p, comision_ext_display: d, comision_ext_pct: (parseFloat(d) || 0) / 100 }));
                    }} />
                  <span className="sc-pct-suffix">%</span>
                </div>
              </div>
              <div className="sc-form-group">
                <label>Comisión Interna %</label>
                <div className="sc-pct-input-wrap">
                  <input type="number" step="0.1" min="0" max="100" placeholder="Ej: 1.5"
                    value={formProy.comision_int_display ?? ''}
                    onChange={e => {
                      const d = e.target.value;
                      setFormProy(p => ({ ...p, comision_int_display: d, comision_int_pct: (parseFloat(d) || 0) / 100 }));
                    }} />
                  <span className="sc-pct-suffix">%</span>
                </div>
              </div>
              <div className="sc-form-group">
                <label>Estado</label>
                <select value={formProy.estado || 'vendido'}
                  onChange={e => setFormProy(p => ({ ...p, estado: e.target.value }))}>
                  <option value="vendido">Vendido</option>
                  <option value="contratado">Contratado</option>
                  <option value="en_ejecucion">En Ejecución</option>
                  <option value="cobrado_liquidado">Cobrado</option>
                  <option value="entregado_liquidado">Entregado</option>
                </select>
              </div>
              {formProy.valor > 0 && (
                <div className="sc-form-group sc-form-full sc-preview-margen">
                  <span>Margen neto: <strong>{fmtPct(margenProyecto(formProy))}</strong></span>
                  <span>$ Margen: <strong>{fmt(formProy.valor * margenProyecto(formProy))}</strong></span>
                </div>
              )}
            </div>

            <div className="sc-modal-footer">
              <button className="sc-btn-cancel" onClick={() => setModalProy(false)} disabled={guardando}>Cancelar</button>
              <button className="sc-btn-save" onClick={guardarProyecto} disabled={guardando}>
                {guardando ? 'Guardando...' : (modalProy === 'edit' ? 'Actualizar' : 'Agregar')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── MODAL CONFIRMAR ELIMINAR ──────────────────────────────────
  const renderConfirmDelete = () => (
    <div className="sc-modal-overlay" onClick={() => setConfirmDelete(null)}>
      <div className="sc-modal sc-modal-confirm" onClick={e => e.stopPropagation()}>
        <div className="sc-modal-header">
          <h3>Eliminar Proyecto</h3>
          <button className="sc-modal-close" onClick={() => setConfirmDelete(null)}>×</button>
        </div>
        <div className="sc-modal-body">
          <p>¿Eliminar <strong>{confirmDelete?.nombre_proyecto}</strong>? Esta acción no se puede deshacer.</p>
          <div className="sc-modal-footer">
            <button className="sc-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancelar</button>
            <button className="sc-btn-danger" onClick={() => eliminarProyecto(confirmDelete.id)}>Eliminar</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {vista === 'anual' ? renderAnual() : renderMensual()}
      {modalMeta   && renderModalMeta()}
      {modalProy   && renderModalProy()}
      {confirmDelete && renderConfirmDelete()}
    </>
  );
}
